import api from "./api";

// Unified facility block/unblock request queue (FacilityBlockRequestController).
// Supersedes the unblock-only flow in facilityUnblockRequestService.js — that
// file's routes (/api/facility-unblock-requests*) still work (the backend
// keeps them as legacy aliases into this same controller/table), but this is
// now the real, current API: an Instructor can request a brand-new block,
// not just lift an existing one, through one shared facility_block_requests
// table distinguished by `request_type` ("block" | "unblock"). Both request
// types share one submit/view/approve/reject permission set
// (submit_facility_block_requests, view_facility_block_requests,
// approve_facility_block_requests, reject_facility_block_requests — see
// ConfigureFacilityBlockRequestFlowSeeder), which also revoked Instructor's
// direct create_facility_blocks/delete_facility_blocks, so this request flow
// is now their only path to either action. FacilityBlocksManagement.jsx is
// the only consumer.

// GET /api/facility-block-requests — admin/director queue, gated by
// "view_facility_block_requests". Optional { status, request_type } filters —
// status defaults to "Pending" server-side if omitted (pass status: "" for
// every status); request_type narrows to "block" or "unblock" (omit for
// both). Returns a plain array, each row: {id, request_type, facility_block_id,
// status_id, status ("Pending"|"Approved"|"Rejected"), requested_by,
// requested_by_name, reviewed_by, reviewed_by_name, reviewed_at, note (the
// submitter's note; overwritten with the reviewer's note if rejected),
// created_at, updated_at, facility_block: {id, facility_id, facility_name,
// facility_slot_id, slot_code, start_date, end_date, start_time, end_time,
// reason, block_status_id, block_status}}.
export const listFacilityBlockRequests = (params = {}) =>
  api.get("/api/facility-block-requests", { params }).then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/facility-block-requests/my-submissions — the logged-in system
// user's own submissions, gated by "submit_facility_block_requests". Optional
// { status, request_type } filters — status defaults to every status (unlike
// the admin queue above) when omitted. Same row shape as
// listFacilityBlockRequests.
export const listMyFacilityBlockRequests = (params = {}) =>
  api.get("/api/facility-block-requests/my-submissions", { params }).then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/facility-block-requests/{id} — single row, same shape as one entry
// from listFacilityBlockRequests above.
export const getFacilityBlockRequest = (id) => api.get(`/api/facility-block-requests/${id}`).then((r) => r.data);

// POST /api/facility-block-requests/block — Instructor requests a brand-new
// block. { facility_id, facility_slot_id?, start_date, end_date, start_time?,
// end_time?, reason, note? }. start_time/end_time must be given together (both
// or neither). Creates the underlying facility_blocks row immediately with
// block_status "Pending" (kept out of bookings the same as an Active block,
// but not "officially" active until approved) plus a Pending request row.
// 422s with `booking_ids` if the period already has pending/confirmed
// bookings. Returns { message, pending: true, ...row fields } (201).
export const submitFacilityBlockRequest = (payload) =>
  api.post("/api/facility-block-requests/block", payload).then((r) => r.data);

// POST /api/facility-block-requests/unblock — request lifting an Active
// block. { facility_block_id, note }. Both required. 422s if the block isn't
// Active, or if a Pending unblock request already exists for it (returns
// { message, request_id } of the existing one — safe to surface directly).
// Returns { message, pending: true, ...row fields } (201).
export const submitFacilityUnblockRequest = (payload) =>
  api.post("/api/facility-block-requests/unblock", payload).then((r) => r.data);

// POST /api/facility-block-requests/{id}/approve — no body. Gated by
// "approve_facility_block_requests". For a block request: flips the
// underlying facility_blocks row to "Active". For an unblock request: flips
// it to "Removed". 422s if the request isn't still Pending, or (block
// requests only) if a conflicting booking has appeared since submission
// (returns `booking_ids`). Returns { message, ...row fields }.
export const approveFacilityBlockRequest = (id) => api.post(`/api/facility-block-requests/${id}/approve`).then((r) => r.data);

// POST /api/facility-block-requests/{id}/reject  { note }. Required — 422s
// without one. Gated by "reject_facility_block_requests". For a block
// request: flips the underlying facility_blocks row to "Rejected" (never
// goes live). For an unblock request: the block stays Active, unaffected.
// 422s if the request isn't still Pending. Returns { message, ...row
// fields }.
export const rejectFacilityBlockRequest = (id, note) =>
  api.post(`/api/facility-block-requests/${id}/reject`, { note }).then((r) => r.data);
